import React, { useState } from "react";
import { MoreHorizontal, type LucideIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";

export interface EntityAction {
  label: string;
  icon?: LucideIcon;
  onClick: () => void;
  destructive?: boolean;
  disabled?: boolean;
  hidden?: boolean;
  requiresConfirmation?: {
    title: string;
    description: string;
  };
}

interface EntityActionsProps {
  actions: EntityAction[];
  size?: "sm" | "default";
  align?: "start" | "end";
  className?: string;
}

/**
 * EntityActions - Universal meatballs menu for entity actions
 * 
 * Features:
 * - Consistent action menu across all entity lists and cards
 * - Automatic grouping: regular actions first, destructive actions last
 * - Built-in confirmation dialogs for destructive actions
 * - Supports disabled and hidden states
 * 
 * Standard action order:
 * 1. View/Open (if in list context)
 * 2. Edit
 * 3. Send/Share actions
 * 4. ---separator---
 * 5. Destructive actions (Delete, Cancel, etc.)
 * 
 * @example
 * <EntityActions
 *   actions={[
 *     { label: "View", icon: Eye, onClick: () => navigate(`/projects/${id}`) },
 *     { label: "Edit", icon: Edit, onClick: handleEdit },
 *     { label: "Delete", icon: Trash2, onClick: handleDelete, destructive: true, 
 *       requiresConfirmation: { title: "Delete Project?", description: "This cannot be undone." } 
 *     }
 *   ]}
 * />
 */
const EntityActions: React.FC<EntityActionsProps> = ({
  actions,
  size = "default",
  align = "end",
  className,
}) => {
  const [confirmingAction, setConfirmingAction] = useState<EntityAction | null>(null);

  // Filter out hidden actions
  const visibleActions = actions.filter(a => !a.hidden);
  
  if (visibleActions.length === 0) {
    return null;
  }

  // Separate regular and destructive actions
  const regularActions = visibleActions.filter(a => !a.destructive);
  const destructiveActions = visibleActions.filter(a => a.destructive);

  const handleActionClick = (action: EntityAction) => {
    if (action.requiresConfirmation) {
      setConfirmingAction(action);
    } else {
      action.onClick();
    }
  };

  const handleConfirm = () => {
    if (confirmingAction) {
      confirmingAction.onClick();
      setConfirmingAction(null);
    }
  };

  const buttonSize = size === "sm" ? "h-8 w-8" : "h-10 w-10";
  const iconSize = size === "sm" ? "h-3.5 w-3.5" : "h-4 w-4";

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button 
            variant="ghost" 
            size="icon" 
            className={cn(buttonSize, className)}
            onClick={(e) => e.stopPropagation()}
          >
            <MoreHorizontal className={iconSize} />
            <span className="sr-only">More actions</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align={align} className="w-48">
          {regularActions.map((action, index) => (
            <DropdownMenuItem
              key={`action-${index}`}
              onClick={(e) => {
                e.stopPropagation();
                handleActionClick(action);
              }}
              disabled={action.disabled}
            >
              {action.icon && <action.icon className="h-4 w-4 mr-2" />}
              {action.label}
            </DropdownMenuItem>
          ))}
          
          {regularActions.length > 0 && destructiveActions.length > 0 && (
            <DropdownMenuSeparator />
          )}
          
          {destructiveActions.map((action, index) => (
            <DropdownMenuItem
              key={`destructive-${index}`}
              onClick={(e) => {
                e.stopPropagation();
                handleActionClick(action);
              }}
              disabled={action.disabled}
              className="text-destructive focus:text-destructive focus:bg-destructive/10"
            >
              {action.icon && <action.icon className="h-4 w-4 mr-2" />}
              {action.label}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Confirmation Dialog */}
      <AlertDialog open={!!confirmingAction} onOpenChange={() => setConfirmingAction(null)}>
        <AlertDialogContent onClick={(e) => e.stopPropagation()}>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmingAction?.requiresConfirmation?.title || "Confirm Action"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmingAction?.requiresConfirmation?.description || "Are you sure you want to continue?"}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={(e) => e.stopPropagation()}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.stopPropagation();
                handleConfirm();
              }}
              className={cn(
                confirmingAction?.destructive && "bg-destructive text-destructive-foreground hover:bg-destructive/90"
              )}
            >
              {confirmingAction?.label || "Confirm"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default EntityActions;
